import React from 'react';
import { createStyles } from '@mantine/core';
import { IconInfoCircle, IconX } from '@tabler/icons-react';

import dt from '~/domains/contest/domains/game/domains/common/testing.const';
import { UnstyledButton } from '~/domains/design-system/Button';
import { textStyles } from '~/domains/design-system/Text';

import type { TierObject } from '../../types/tier';

import { PlayerCardInfo } from './sections/PlayerCardInfo';
import { PlayerCardHeader } from './sections/PlayerCardHeader';
import { PlayerCardAfterGameStart } from './variants/PlayerCardAfterGameStart';
import { PlayerCardUpcoming } from './variants/PlayerCardUpcoming';
import { PlayerCardEmpty } from './variants/PlayerCardEmpty';
import { PlayerCardUpcomingPlaceholder } from './variants/PlayerCardUpcomingPlaceholder';
import { PlayerCardEmptyPick } from './variants/PlayerCardEmptyPick';
import PlayerCardDropYourWorst from './sections/PlayerCardDropYourWorst';
import PlayerCardAlternate from './sections/PlayerCardAlternate';

const useStyles = createStyles((theme, _, getRef) => ({
  container: {
    position: 'relative',
    background: theme.white,
    borderRadius: theme.other.spacing._8,
    minWidth: 170,
    border: `2px solid ${theme.white}`,
    boxShadow: '0 2px 10px 0 rgba(0, 0, 0, 0.05)',

    '&.isClickable': {
      transition: 'border-color 150ms ease-in-out',

      '&:hover': {
        borderColor: theme.colors.blue[4],
      },
    },

    '&.isSelected': {
      borderColor: theme.colors.blue[4],
    },

    '&.primaryColor': {
      background: theme.colors.blue[4],
    },
  },
  infoIcon: {
    position: 'absolute',
    top: theme.other.spacing._2,
    left: theme.other.spacing._2,
    zIndex: 4,
    color: theme.colors.gray2[3],
    transition: 'color 150ms ease-in-out',
    padding: theme.other.spacing._4,

    '&:hover': {
      color: theme.colors.blue[7],
    },
  },
  remove: {
    position: 'absolute',
    right: -10,
    top: -10,
    cursor: 'pointer',
    padding: 0,
    background: theme.black,
    border: `2px solid ${theme.colors.gray[1]}`,
    borderRadius: '100em',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 4,
    width: 24 + 4,
    height: 24 + 4,
    transition: 'background-color 150ms ease-in-out',

    '&:hover, &:focus': {
      background: theme.colors.red[6],
    },
  },
  content: {
    ref: getRef('content'),
    position: 'relative',
    display: 'block',
    width: '100%',
    textAlign: 'left',
    cursor: 'default',

    '&.isClickable': {
      cursor: 'pointer',
    },
  },
  pickedCount: {
    borderTop: `1px solid ${theme.colors.gray2[0]}`,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...textStyles['subhead-x-small'],
    color: theme.colors.gray2[4],
  },
}));

type GamecenterPlayerCardProps = {
  children: React.ReactNode;
  dropWorstNth?: number;
  isLocked?: boolean;
  isRemoveable?: boolean;
  isSelectable?: boolean;
  isSelected?: boolean;
  isTier?: boolean;
  isUpcomingPlaceholder?: boolean;
  pickId?: string;
  playerId?: string;
  playerImage?: string;
  playerPrimaryColor?: string;
  primaryPosition?: string;
  tag?: {
    position?: string;
    salary?: number;
    injury?: string;
    playerTier?: string;
  };
  tier?: TierObject | { id: string };
  pickedCount?: {
    value: number;
    max: number;
  };
  isAlternate?: boolean;
  withoutTier?: boolean;

  onCardRemoveClick?: (tier: TierObject | { id: string }) => void;
  onCardClick?: (tier: TierObject | { id: string }, pickId: string, isLocked?: boolean) => void;
  onPlayerInfoClick?: (
    pickId: string,
    tier: TierObject | { id: string },
    isLocked?: boolean
  ) => void;
};

function GamecenterPlayerCard({
  children,
  dropWorstNth,
  isLocked,
  isRemoveable,
  isSelected,
  isSelectable,
  isTier,
  isUpcomingPlaceholder,
  onPlayerInfoClick,
  onCardRemoveClick,
  onCardClick,
  pickId,
  playerId,
  playerImage,
  playerPrimaryColor,
  primaryPosition,
  tag,
  tier,
  pickedCount,
  isAlternate,
  withoutTier,
}: GamecenterPlayerCardProps) {
  const { classes, cx, theme } = useStyles();
  const isClickable = isSelectable || (!pickId && !isUpcomingPlaceholder);

  return (
    <div
      className={cx(classes.container, {
        isClickable,
        isSelected,
      })}
      aria-selected={isSelected}
      data-test-id={dt.dfs.playerCard.container}
      data-player-id={playerId}
    >
      {!isTier && dropWorstNth ? <PlayerCardDropYourWorst dropWorstNth={dropWorstNth} /> : null}
      {isAlternate && <PlayerCardAlternate />}
      {isRemoveable ? (
        <UnstyledButton
          data-test-id={dt.dfs.playerCard.remove}
          onClick={() => {
            onCardRemoveClick?.(tier);
          }}
          className={classes.remove}
        >
          <IconX size={14} stroke={4} color="white" />
        </UnstyledButton>
      ) : null}
      {onPlayerInfoClick && !!playerId && (
        <UnstyledButton
          className={classes.infoIcon}
          data-test-id={dt.dfs.playerCard.infoButton}
          onClick={() => onPlayerInfoClick(playerId, tier, isLocked)}
        >
          <IconInfoCircle fill={theme.white} />
        </UnstyledButton>
      )}
      <UnstyledButton
        data-test-id={dt.dfs.playerCard.draftButton}
        className={cx(classes.content, {
          isClickable,
        })}
        onClick={() => {
          if (isUpcomingPlaceholder) return;
          onCardClick?.(tier, pickId, isLocked);
        }}
      >
        <PlayerCardHeader
          primaryColor={playerPrimaryColor}
          playerImage={playerImage}
          tag={isTier || withoutTier ? { ...tag, playerTier: undefined } : tag}
        />
        <PlayerCardInfo withoutTier={withoutTier} isTier={isTier} isLocked={isLocked}>
          {children}
        </PlayerCardInfo>
        {pickedCount ? (
          <div className={classes.pickedCount}>
            Picked: {pickedCount.value + (isSelected ? 1 : 0)}/{pickedCount.max}
          </div>
        ) : null}
      </UnstyledButton>
    </div>
  );
}

GamecenterPlayerCard.Empty = PlayerCardEmpty;
GamecenterPlayerCard.UpcomingPlaceholder = PlayerCardUpcomingPlaceholder;
GamecenterPlayerCard.Upcoming = PlayerCardUpcoming;
GamecenterPlayerCard.EmptyPick = PlayerCardEmptyPick;
GamecenterPlayerCard.AfterGameStart = PlayerCardAfterGameStart;

export default GamecenterPlayerCard;
